import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { FeatureHelperService } from '../feature-helper/feature-helper.service';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { hasRole } from './current-user-roles';

/**
 * The one "can this user use it?" check, the same rule the API applies: the caller holds the role feature
 * (enabled) AND the feature switch is on for them AND, when a role is given, they hold that role (System Controls:
 * ADMIN). Any part may be left out. Fails closed.
 *
 * The role feature is read from FeatureHelperService once AuthGuard's refresh from the server has answered, so a
 * grant changed on Role Features counts on the next navigation. The switch comes from FeatureSwitchService, which
 * refetches on every navigation.
 */
@Injectable({ providedIn: 'root' })
export class CanUseService {

  constructor(
    private featureHelperService: FeatureHelperService,
    private featureSwitches: FeatureSwitchService
  ) { }

  public canUse(featureName?: string, switchKey?: string, role?: string): Observable<boolean> {
    return this.featureHelperService.settled().pipe(
      switchMap(() => {
        if (role && !hasRole(role)) {
          return of(false);
        }
        if (featureName && !this.featureHelperService.hasFeature(featureName)) {
          return of(false);
        }
        return switchKey ? this.featureSwitches.isEnabled(switchKey) : of(true);
      }),
      catchError(() => of(false))
    );
  }
}
